import {
  FileText,
  Image,
  Video,
  AudioLines,
  Type,
  Calculator,
  QrCode,
  Briefcase,
  Palette,
  Shield,
  Search,
  FolderArchive,
  Sparkles,
  Landmark,
  PiggyBank,
  HeartPulse,
  Box,
  ShoppingCart,
  Plane,
  PartyPopper,
  Smile,
  Home,
  GraduationCap,
  Truck,
  Megaphone,
  Presentation,
} from "lucide-react";

export interface Tool {
  id: string;
  name: string;
  description: string;
  category: string;
  isPremium?: boolean;
  isAI?: boolean;
  /** Set only for tools whose logic is tied to one country's specific
   * rules (tax brackets, government forms, etc.) -- not a general quality
   * flag, just an honest heads-up that this tool won't apply the same way
   * everywhere. */
  region?: string;
  /** Excluded from browsing/discovery (category grids, search, sitemap,
   * footer index, related-tools suggestions) while still resolvable by
   * direct ID lookup -- the same convention used for hidden categories,
   * just at the individual-tool level. For a tool whose real-world
   * reliability (a network-dependent AI model, in practice) doesn't match
   * the "just works" bar the rest of the free tools hold to. */
  hidden?: boolean;
}

export interface Category {
  id: string;
  name: string;
  description: string;
  icon: typeof FileText;
  color: string;
  tools: Tool[];
  /** Excluded from browsing/discovery (nav, homepage grid, footer index,
   * sitemap, search) while still resolvable by direct ID lookup. Used for
   * AI Studio, which is intentionally not offered on the public site yet -- keeps the data
   * model intact for when it is, without exposing fake/coming-soon tools. */
  hidden?: boolean;
}

export const categories: Category[] = [
  {
    id: "pdf-tools",
    name: "PDF Tools",
    description:
      "Merge, split, compress, convert, and edit PDF files directly in your browser.",
    icon: FileText,
    color: "oklch(0.55 0.2 25)",
    tools: [
      {
        id: "merge-pdf",
        name: "Merge PDF",
        description: "Combine multiple PDF files into one document",
        category: "pdf-tools",
      },
      {
        id: "split-pdf",
        name: "Split PDF",
        description: "Extract pages or split a PDF into multiple files",
        category: "pdf-tools",
      },
      {
        id: "compress-pdf",
        name: "Compress PDF",
        description: "Shrink a PDF in four modes: Balanced, Aggressive, Extreme or a target size like 2 MB",
        category: "pdf-tools",
      },
      {
        id: "rotate-pdf",
        name: "Rotate PDF",
        description: "Rotate PDF pages to any orientation",
        category: "pdf-tools",
      },
      {
        id: "pdf-to-word",
        name: "PDF to Word",
        description: "Convert PDF documents to editable Word files",
        category: "pdf-tools",
      },
      {
        id: "pdf-to-jpg",
        name: "PDF to JPG",
        description: "Convert one or several PDFs to high-quality JPG images at once",
        category: "pdf-tools",
      },
      {
        id: "pdf-to-excel",
        name: "PDF to Excel",
        description: "Extract tables from PDF to Excel spreadsheets",
        category: "pdf-tools",
      },
      {
        id: "word-to-pdf",
        name: "Word to PDF",
        description: "Convert Word documents to PDF format",
        category: "pdf-tools",
      },
      {
        id: "excel-to-pdf",
        name: "Excel to PDF",
        description: "Convert spreadsheet data into a real, readable PDF table",
        category: "pdf-tools",
      },
      {
        id: "image-to-pdf",
        name: "Image to PDF",
        description: "Convert one or more images into a single PDF document",
        category: "pdf-tools",
      },
      {
        id: "pdf-password",
        name: "Add PDF Password",
        description: "Add real password protection to a PDF, right in your browser",
        category: "pdf-tools",
      },
      {
        id: "pdf-watermark",
        name: "Add Watermark",
        description: "Add text or image watermarks to PDF files",
        category: "pdf-tools",
      },
      {
        id: "extract-pdf",
        name: "Extract Pages & Text",
        description: "Pull specific pages or the text content out of a PDF",
        category: "pdf-tools",
      },
      {
        id: "organize-pdf",
        name: "Organize PDF",
        description: "Reorder or remove pages using real page previews",
        category: "pdf-tools",
      },
      {
        id: "add-page-numbers",
        name: "Add Page Numbers",
        description: "Number every page of a PDF, with your choice of position and starting number",
        category: "pdf-tools",
      },
      {
        id: "legal-production-desk",
        name: "Legal Production Desk",
        description: "Prepare a document production: running Bates ranges, one-click stamping of every PDF into a ZIP, production log, privilege log and cover letter. Monthly plan with a 7-day trial.",
        category: "pdf-tools",
      },
      {
        id: "exhibit-stamper",
        name: "Exhibit Stamper",
        description: "Stamp Exhibit A, B, C or 1, 2, 3 labels on a set of PDFs and get an index of which document is which exhibit.",
        category: "pdf-tools",
      },
      {
        id: "bates-numbering",
        name: "Bates Numbering",
        description: "Stamp a running document number such as ABC000001 on every page of one or more PDFs, with an optional second line and an index CSV.",
        category: "pdf-tools",
      },
      {
        id: "book-writing-toolbox",
        name: "Book Writing Toolbox",
        description: "Write your book directly in a real rich-text editor, preview it instantly with professional typesetting, and download when it's ready",
        category: "pdf-tools",
      },
      {
        id: "magazine-maker",
        name: "Magazine Maker",
        description: "Write articles (text, one picture each) or combine finished PDF, Word and image files into a magazine issue with a cover and an 'In this issue' contents page",
        category: "pdf-tools",
      },
      {
        id: "catalog-maker",
        name: "Catalog Maker",
        description: "Assemble product sheets and photos into a complete catalog with cover, price list, and auto-generated Table of Contents",
        category: "pdf-tools",
      },
      {
        id: "document-assembler",
        name: "Document Assembler",
        description: "Combine PDF, Word, and image files into one properly assembled document with real pagination",
        category: "pdf-tools",
      },
      {
        id: "redact-pdf",
        name: "Redact PDF",
        description: "Permanently remove sensitive content from a PDF — true removal, not just a black box on top; the rest of the page stays real text",
        category: "pdf-tools",
      },
      {
        id: "ocr-pdf",
        name: "OCR PDF",
        description: "Make a scanned PDF searchable and its text selectable, without changing how it looks",
        category: "pdf-tools",
      },
      {
        id: "html-to-pdf",
        name: "HTML to PDF",
        description: "Convert HTML content into a PDF document",
        category: "pdf-tools",
      },
      {
        id: "fillable-forms",
        name: "Fillable Forms",
        description: "Fill in a PDF's existing form fields — text, checkboxes, dropdowns, and radio groups",
        category: "pdf-tools",
      },
      {
        id: "flatten-pdf",
        name: "Flatten PDF",
        description: "Permanently lock in a PDF's filled-in form field values so they can no longer be edited",
        category: "pdf-tools",
      },
      {
        id: "compare-pdf",
        name: "Compare PDF",
        description: "See exactly what text changed between two versions of a PDF, redline-style",
        category: "pdf-tools",
      },
      {
        id: "repair-pdf",
        name: "Repair PDF",
        description: "Attempt to recover and rebuild a corrupted or broken PDF as a clean file",
        category: "pdf-tools",
      },
      {
        id: "crop-pdf",
        name: "Crop PDF",
        description: "Crop every page of a PDF to a region you select",
        category: "pdf-tools",
      },
      {
        id: "pdf-editor",
        name: "PDF Editor",
        description: "Add annotations, highlights, and notes to PDFs",
        category: "pdf-tools",
      },
      {
        id: "sign-pdf",
        name: "Sign PDF",
        description: "Draw and place your signature on a PDF document",
        category: "pdf-tools",
      },
      {
        id: "unlock-pdf",
        name: "Unlock PDF",
        description: "Remove the password from a PDF you can open",
        category: "pdf-tools",
      },
      {
        id: "remove-pdf-pages",
        name: "Remove PDF Pages",
        description: "Delete unwanted pages from a PDF by clicking thumbnails or typing page numbers",
        category: "pdf-tools",
      },
      {
        id: "low-content-interior-maker",
        name: "Low-Content Book Interior Maker",
        description: "Make lined, dot grid, graph, checklist or blank interior pages for notebooks, journals and planners, with the right inside margin for your page count.",
        category: "pdf-tools",
      },
      {
        id: "book-interior-maker",
        name: "Book Interior Maker",
        description: "Turn manuscript chapters into a print-style interior PDF with trim size, margins, page numbers, running heads and a page-flip preview.",
        category: "pdf-tools",
      },
      {
        id: "copyright-page-maker",
        name: "Copyright Page Maker",
        description: "Build a book's copyright page from a form and get a one-page PDF at your trim size, plus copyable text and HTML.",
        category: "pdf-tools",
      },
      {
        id: "pdf-bookmarks-editor",
        name: "PDF Bookmarks Editor",
        description: "Edit a PDF's bookmarks as a tree: rename, reorder, indent, delete or add one at a page, then download the PDF.",
        category: "pdf-tools",
      },
    ],
  },
  {
    id: "powerpoint-slides",
    name: "PowerPoint & Slides",
    description:
      "Convert, split, merge, compress, organize, clean, extract from and build PowerPoint presentations directly in your browser.",
    icon: Presentation,
    color: "oklch(0.58 0.19 45)",
    tools: [
      {
        id: "powerpoint-to-pdf",
        name: "PowerPoint to PDF",
        description: "Convert presentation slides into a PDF, one page per slide",
        category: "powerpoint-slides",
      },
      {
        id: "powerpoint-to-images",
        name: "PowerPoint to Images",
        description: "Save every slide of a presentation as its own JPG or PNG picture",
        category: "powerpoint-slides",
      },
      {
        id: "extract-from-powerpoint",
        name: "Extract from PowerPoint",
        description: "Pull the original pictures, text or speaker notes out of a .pptx",
        category: "powerpoint-slides",
      },
      {
        id: "clean-powerpoint",
        name: "Clean PowerPoint for Sharing",
        description: "Remove notes, comments, hidden slides and author details before you send a deck",
        category: "powerpoint-slides",
      },
      {
        id: "images-to-powerpoint",
        name: "Images to PowerPoint",
        description: "Make a presentation with one picture per slide, in the order you choose",
        category: "powerpoint-slides",
      },
      {
        id: "markdown-to-powerpoint",
        name: "Markdown to PowerPoint",
        description: "Write slides as plain text and get a .pptx with notes, tables and themes",
        category: "powerpoint-slides",
      },
      {
        id: "pdf-to-powerpoint",
        name: "PDF to PowerPoint",
        description: "Turn PDF pages into slides, as pictures or as best-effort editable text",
        category: "powerpoint-slides",
      },
      {
        id: "split-powerpoint",
        name: "Split PowerPoint",
        description: "Split a presentation by slide range, every N slides, or one file per slide",
        category: "powerpoint-slides",
      },
      {
        id: "compress-powerpoint",
        name: "Compress PowerPoint",
        description: "Make a presentation smaller by re-saving its pictures, with real sizes shown",
        category: "powerpoint-slides",
      },
      {
        id: "organize-slides",
        name: "Organize Slides",
        description: "Reorder, duplicate and delete slides, or remove all hidden slides",
        category: "powerpoint-slides",
      },
      {
        id: "merge-powerpoint",
        name: "Merge PowerPoint",
        description: "Combine presentations into one, each keeping its own design",
        category: "powerpoint-slides",
      },
      {
        id: "find-replace-powerpoint",
        name: "Find and Replace in PowerPoint",
        description: "Find text across every slide and replace it, with a count and a preview per slide",
        category: "powerpoint-slides",
      },
      {
        id: "replace-fonts-powerpoint",
        name: "Replace Fonts in PowerPoint",
        description: "See every font a deck uses and swap any of them for another in one go",
        category: "powerpoint-slides",
      },
      {
        id: "add-slide-numbers",
        name: "Add Slide Numbers to PowerPoint",
        description: "Number every slide in the corner you choose, as real slide-number fields",
        category: "powerpoint-slides",
      },
      {
        id: "watermark-powerpoint",
        name: "Watermark PowerPoint",
        description: "Stamp DRAFT, CONFIDENTIAL or a logo on every slide, behind or over the content",
        category: "powerpoint-slides",
      },
      {
        id: "resize-slides",
        name: "Resize PowerPoint Slides",
        description: "Change a deck to 4:3, 16:9, A4 or a custom size and scale the content to match",
        category: "powerpoint-slides",
      },
      {
        id: "accessibility-checker-powerpoint",
        name: "PowerPoint Accessibility Checker",
        description: "Find missing alt text, empty titles and tiny text, and add the fixes you type",
        category: "powerpoint-slides",
      },
      {
        id: "extract-charts-powerpoint",
        name: "Extract Charts From PowerPoint",
        description: "Get the numbers behind every chart in a deck as Excel or CSV",
        category: "powerpoint-slides",
      },
      {
        id: "word-to-powerpoint",
        name: "Word to PowerPoint",
        description: "Turn a Word document's headings and paragraphs into slides, with an outline you can edit",
        category: "powerpoint-slides",
      },
      {
        id: "excel-to-powerpoint",
        name: "Excel to PowerPoint",
        description: "Turn spreadsheet tables into table slides with the header repeated",
        category: "powerpoint-slides",
      },
      {
        id: "slide-deck-maker",
        name: "Slide Deck Maker",
        description: "Build a presentation step by step from your own text, with layouts, themes and your brand colour",
        category: "powerpoint-slides",
      },
      {
        id: "photo-slideshow-maker",
        name: "Photo Slideshow Maker",
        description: "Make a self-running photo slideshow with transitions and timing",
        category: "powerpoint-slides",
      },
      {
        id: "repair-powerpoint",
        name: "Repair PowerPoint File",
        description: "Mend a .pptx that will not open, and see what was fixed and what was lost",
        category: "powerpoint-slides",
      },
      {
        id: "redact-powerpoint",
        name: "Redact PowerPoint",
        description: "Black out emails, numbers and words in slides, notes and pictures, then check what is left",
        category: "powerpoint-slides",
      },
      {
        id: "powerpoint-to-html",
        name: "PowerPoint to HTML",
        description: "Turn a presentation into one web page file that opens in any browser",
        category: "powerpoint-slides",
      },
      {
        id: "powerpoint-to-video",
        name: "PowerPoint to Video",
        description: "Turn a presentation into an MP4 with the time per slide and optional music",
        category: "powerpoint-slides",
      },
      {
        id: "powerpoint-to-gif",
        name: "PowerPoint to GIF",
        description: "Turn a short presentation into an animated GIF",
        category: "powerpoint-slides",
      },
      {
        id: "powerpoint-templates",
        name: "PowerPoint Templates",
        description: "Starter decks for a pitch, review, kickoff and more, as .pptx or .potx",
        category: "powerpoint-slides",
      },
      {
        id: "template-theme-maker",
        name: "PowerPoint Template and Theme Maker",
        description: "Turn a deck into a .potx template and change its colours, fonts and theme name",
        category: "powerpoint-slides",
      },
      {
        id: "password-protect-powerpoint",
        name: "Password Protect PowerPoint",
        description: "Lock a .pptx with a password, or unlock one you have the password for",
        category: "powerpoint-slides",
      },
      {
        id: "presentation-studio",
        name: "Presentation Studio",
        description: "Finish a deck in one pass: size, numbers, watermark, accessibility check, cleaning and compression",
        category: "powerpoint-slides",
      },
    ],
  },
  {
    id: "image-tools",
    name: "Image Tools",
    description:
      "Compress, resize, convert, and enhance images with powerful browser-based tools.",
    icon: Image,
    color: "oklch(0.6 0.18 145)",
    tools: [
      {
        id: "compress-image",
        name: "Compress Image",
        description: "Reduce image file size by at least the percentage you choose",
        category: "image-tools",
      },
      {
        id: "resize-crop",
        name: "Resize & Crop",
        description: "Resize or crop images to exact dimensions, with five fit modes, rotate, mirror and black and white",
        category: "image-tools",
      },
      {
        id: "convert-image",
        name: "Convert Image Format",
        description: "Convert images between PNG, JPG, WebP, AVIF, GIF, BMP, ICO and TIFF, with resize, rotate and more",
        category: "image-tools",
      },
      {
        id: "remove-background",
        name: "Remove Background",
        description: "Background removal that runs entirely in your browser — photo never uploaded",
        category: "image-tools",
        isAI: true,
      },
      {
        id: "flip-rotate-image",
        name: "Flip & Rotate",
        description: "Flip images horizontally/vertically or rotate in 90-degree steps",
        category: "image-tools",
      },
      {
        id: "image-filters",
        name: "Add Filters & Effects",
        description: "Apply real browser-based filters and effects to images",
        category: "image-tools",
      },
      {
        id: "image-quality-enhancer",
        name: "Image Quality Enhancer",
        description: "Sharpen, boost clarity, fix color cast, brighten shadows, or upscale an unclear or low-resolution photo",
        category: "image-tools",
      },
      {
        id: "document-scanner",
        name: "Document Scanner",
        description: "Turn a photo of a document into a clean scanned look — crop, straighten, rotate, and save as PDF",
        category: "image-tools",
      },
      {
        id: "camera-capture",
        name: "Camera Capture",
        description: "Take a photo with your webcam or device camera, and optionally crop it",
        category: "image-tools",
      },
      {
        id: "bulk-image",
        name: "Bulk Compress & Convert",
        description: "Compress and convert multiple images at once to PNG, JPG, WebP, AVIF, GIF, BMP, ICO or TIFF, with resize, rotate and more",
        category: "image-tools",
      },
      {
        id: "webp-converter",
        name: "WebP Converter",
        description: "Convert images to WebP for smaller file sizes, or from WebP back to JPG/PNG, with resize and black and white",
        category: "image-tools",
      },
      {
        id: "passport-photo",
        name: "Passport Photo Maker",
        description:
          "Create passport and visa photos with standard size presets and print sheets",
        category: "image-tools",
      },
      {
        id: "screenshot-tool",
        name: "Screenshot Tool",
        description:
          "Capture a screenshot, crop it, rotate it, and draw or highlight on it before copying or downloading",
        category: "image-tools",
      },
      {
        id: "collage-maker",
        name: "Photo Collage Maker",
        description:
          "Combine 2-9 photos into a single collage with layout options",
        category: "image-tools",
      },
      {
        id: "exif-remover",
        name: "EXIF Remover",
        description: "See and remove hidden location, camera and date data from JPEG, PNG and WebP pictures",
        category: "image-tools",
      },
      {
        id: "image-splitter",
        name: "Image Splitter",
        description: "Cut a picture into a grid of equal tiles for Instagram grids, posters and puzzles",
        category: "image-tools",
      },
    ],
  },
  {
    id: "video-tools",
    name: "Video Tools",
    description:
      "Compress, convert, trim, and process videos right in your browser.",
    icon: Video,
    color: "oklch(0.55 0.2 310)",
    tools: [
      {
        id: "compress-video",
        name: "Compress Video",
        description: "Reduce video file size for sharing",
        category: "video-tools",
      },
      {
        id: "convert-video",
        name: "Format Converter",
        description: "Convert video between MP4, WebM and MOV and keep the quality",
        category: "video-tools",
      },
      {
        id: "trim-video",
        name: "Trim & Cut",
        description: "Cut multiple segments from a video, download individually or merged",
        category: "video-tools",
      },
      {
        id: "merge-video",
        name: "Merge Videos",
        description: "Combine multiple video clips into one",
        category: "video-tools",
      },
      {
        id: "video-to-gif",
        name: "Video to GIF",
        description: "Create animated GIFs from video clips",
        category: "video-tools",
      },
      {
        id: "thumbnail-gen",
        name: "Thumbnail Generator",
        description: "Generate video thumbnails and previews",
        category: "video-tools",
      },
      {
        id: "subtitle-adder",
        name: "Subtitle Adder",
        description: "Add subtitles and captions to videos",
        category: "video-tools",
      },
      {
        id: "screen-recorder",
        name: "Screen Recorder",
        description:
          "Record your screen, a window, or a browser tab — optionally just a selected region",
        category: "video-tools",
      },
      {
        id: "mute-video",
        name: "Mute Video",
        description: "Remove the sound from a video without losing picture quality",
        category: "video-tools",
      },
      {
        id: "rotate-video",
        name: "Rotate Video",
        description: "Rotate or flip a video 90, 180 degrees or mirror it, in your browser",
        category: "video-tools",
      },
      {
        id: "video-speed-changer",
        name: "Video Speed Changer",
        description: "Speed up or slow down a video from 0.25x to 4x with sound kept in tune",
        category: "video-tools",
      },
      {
        id: "reverse-video",
        name: "Reverse Video",
        description: "Play a short video backwards, picture and sound, in your browser",
        category: "video-tools",
      },
    ],
  },
  {
    id: "audio-tools",
    name: "Audio Tools",
    description:
      "Record, trim, convert, and clean up audio right in your browser.",
    icon: AudioLines,
    color: "oklch(0.6 0.2 25)",
    tools: [
      {
        id: "audio-to-subtitles",
        name: "Audio to Subtitles",
        description: "Turn English speech in an audio or video file into SRT or VTT subtitles, on your device. The recording is not uploaded.",
        category: "audio-tools",
      },
      {
        id: "extract-audio",
        name: "Extract Audio",
        description: "Extract audio tracks from video files",
        category: "audio-tools",
      },
      {
        id: "audio-trimmer",
        name: "Audio Trimmer",
        description: "Cut an audio clip down to a specific start and end point",
        category: "audio-tools",
      },
      {
        id: "audio-converter",
        name: "Audio Converter",
        description: "Convert between MP3, WAV, OGG, M4A, and FLAC",
        category: "audio-tools",
      },
      {
        id: "audio-merger",
        name: "Audio Merger",
        description: "Combine multiple audio clips into one file",
        category: "audio-tools",
      },
      {
        id: "audio-compressor",
        name: "Audio Compressor",
        description: "Reduce audio file size by adjusting bitrate",
        category: "audio-tools",
      },
      {
        id: "voice-recorder",
        name: "Voice Recorder",
        description: "Record audio from your microphone and download it",
        category: "audio-tools",
      },
      {
        id: "audio-speed-changer",
        name: "Audio Speed Changer",
        description: "Speed up or slow down audio without changing its pitch",
        category: "audio-tools",
      },
      {
        id: "silence-remover",
        name: "Silence Remover",
        description: "Automatically trim silent sections from an audio file",
        category: "audio-tools",
      },
      {
        id: "audio-normalizer",
        name: "Audio Volume Normalizer",
        description: "Even out volume levels across an audio file",
        category: "audio-tools",
      },
      {
        id: "acx-audiobook-checker",
        name: "ACX Audiobook Checker",
        description: "Check an audiobook chapter against the ACX numbers: loudness, peak, noise floor, sample rate and MP3 bit rate, then fix and save a compliant MP3.",
        category: "audio-tools",
      },
      {
        id: "audio-comparer",
        name: "Compare Two Audio Files",
        description: "Compare two audio clips and see a similarity percentage",
        category: "audio-tools",
      },
      {
        id: "noise-remover",
        name: "Background Noise Remover",
        description: "Reduce steady background hum, hiss, and fan noise",
        category: "audio-tools",
      },
      {
        id: "audio-scrambler",
        name: "Audio Scrambler",
        description: "Disguise a voice by shifting its pitch and tone",
        category: "audio-tools",
      },
      {
        id: "ringtone-maker",
        name: "Ringtone Maker",
        description: "Cut your favourite part of a song into an iPhone M4R or Android MP3 ringtone",
        category: "audio-tools",
      },
      {
        id: "volume-booster",
        name: "Volume Booster",
        description: "Make audio louder or quieter by a set number of dB, with distortion protection",
        category: "audio-tools",
      },
    ],
  },
  {
    id: "text-tools",
    name: "Text & Writing",
    description:
      "Count words, convert case, paraphrase, and transform text effortlessly.",
    icon: Type,
    color: "oklch(0.55 0.15 85)",
    tools: [
      {
        id: "word-counter",
        name: "Word & Character Counter",
        description: "Count words, characters, sentences, and paragraphs",
        category: "text-tools",
      },
      {
        id: "case-converter",
        name: "Case Converter",
        description:
          "Convert text to uppercase, lowercase, title case, and more",
        category: "text-tools",
      },
      {
        id: "text-paraphraser",
        name: "Paraphraser / Rewriter",
        description: "Rewrite text using local synonym swapping",
        category: "text-tools",
      },
      {
        id: "grammar-checker",
        name: "Grammar Checker",
        description: "Check and fix grammar and spelling errors",
        category: "text-tools",
      },
      {
        id: "lorem-ipsum",
        name: "Lorem Ipsum Generator",
        description: "Generate placeholder text for designs",
        category: "text-tools",
      },
      {
        id: "find-replace",
        name: "Find & Replace",
        description: "Find and replace text with regex support",
        category: "text-tools",
      },
      {
        id: "text-reverser",
        name: "Text Reverser / Deduplicator",
        description: "Reverse text, or remove duplicate words or lines",
        category: "text-tools",
      },
      {
        id: "text-to-speech",
        name: "Text to Speech",
        description: "Read text aloud with your device's built-in voices",
        category: "text-tools",
      },
      {
        id: "fancy-font-generator",
        name: "Fancy Font Generator",
        description:
          "Convert text into stylish Unicode fonts for bios and social media",
        category: "text-tools",
      },
      {
        id: "invisible-text-generator",
        name: "Invisible Text Generator",
        description:
          "Generate invisible characters for blank bios and messages",
        category: "text-tools",
      },
      {
        id: "text-sorter",
        name: "Text Sorter",
        description:
          "Sort lines alphabetically or by length, remove duplicates and blanks",
        category: "text-tools",
      },
      {
        id: "email-extractor",
        name: "Email Extractor",
        description: "Pull all email addresses out of any block of text",
        category: "text-tools",
      },
      {
        id: "url-extractor",
        name: "URL Extractor",
        description: "Pull all links out of any block of text or HTML",
        category: "text-tools",
      },
      {
        id: "slug-generator",
        name: "Slug Generator",
        description: "Convert titles into clean, URL-friendly slugs",
        category: "text-tools",
      },
      {
        id: "reading-level-checker",
        name: "Reading Level Checker",
        description:
          "Check Flesch reading ease score and grade level of your writing",
        category: "text-tools",
      },
      {
        id: "ascii-art-generator",
        name: "ASCII Art Generator",
        description: "Turn any text into ASCII banner art",
        category: "text-tools",
      },
      {
        id: "markdown-editor",
        name: "Markdown Editor",
        description: "Write Markdown with a formatting toolbar and live preview, then download it as a .md file or an HTML page",
        category: "text-tools",
      },
      {
        id: "markdown-html-converter",
        name: "Markdown ↔ HTML Converter",
        description: "Convert between Markdown and HTML instantly",
        category: "text-tools",
      },
      {
        id: "text-cleaner",
        name: "Text Cleaner",
        description: "Remove extra spaces, blank lines, curly quotes, invisible characters and HTML from pasted text",
        category: "text-tools",
      },
      {
        id: "number-to-words",
        name: "Number to Words",
        description: "Write any number in English words, with ordinals and cheque-style amounts",
        category: "text-tools",
      },
      {
        id: "morse-code-translator",
        name: "Morse Code Translator",
        description: "Translate text to Morse code and back, and play it as beeps at your chosen speed",
        category: "text-tools",
      },
      {
        id: "book-page-estimator",
        name: "Book Page Count Estimator",
        description: "Estimate printed page count from word count, trim size, type and margins, or work backward from a page target to words.",
        category: "text-tools",
      },
      {
        id: "kdp-description-formatter",
        name: "Book Description HTML Formatter",
        description: "Turn plain text into the HTML tags a book store description accepts, or clean existing HTML, with a preview and a 4,000-character check.",
        category: "text-tools",
      },
      {
        id: "isbn-checker",
        name: "ISBN Checker and Converter",
        description: "Validate ISBN-10 and ISBN-13 check digits, convert between them, add hyphens and see the barcode digits, singly or in bulk.",
        category: "text-tools",
      },
      {
        id: "manuscript-formatter",
        name: "Manuscript Formatter (Shunn)",
        description: "Turn pasted text or .docx, .md and .txt chapters into a standard Shunn-style manuscript .docx with a title page and running head.",
        category: "text-tools",
      },
    ],
  },
  {
    id: "converters",
    name: "Converters & Calculators",
    description:
      "Convert units, currencies, and time zones. Calculate tips, ages, and recipes.",
    icon: Calculator,
    color: "oklch(0.6 0.15 45)",
    tools: [
      {
        id: "heic-to-jpg",
        name: "HEIC to JPG Converter",
        description: "Turn iPhone HEIC photos into JPG, PNG, WebP or PDF in your browser. Nothing is uploaded.",
        category: "converters",
      },
      {
        id: "universal-file-converter",
        name: "Universal File Converter",
        description: "Drop an image, PDF, Excel/CSV, Word, PowerPoint, HTML or text file and see every real format it can become",
        category: "converters",
      },
      {
        id: "unit-converter",
        name: "Unit Converter",
        description: "Convert length, weight, temperature, and more",
        category: "converters",
      },
      {
        id: "currency-converter",
        name: "Currency Converter",
        description: "Convert currencies with the latest daily exchange rates",
        category: "converters",
      },
      {
        id: "timezone-converter",
        name: "Time Zone Converter",
        description: "Convert times between any time zones, and see your event agenda in each zone",
        category: "converters",
      },
      {
        id: "tip-calculator",
        name: "Tip Calculator",
        description: "Calculate tips and split bills easily",
        category: "converters",
      },
      {
        id: "business-days-calculator",
        name: "Business Days Calculator",
        description: "Count working days between two dates or add working days to find a deadline, with your own weekend and holidays",
        category: "converters",
      },
      {
        id: "online-timer",
        name: "Online Timer",
        description: "Countdown timer with 1, 5, 10 and 20 minute presets, a custom time, a Pomodoro mode and a floating pop-out view",
        category: "converters",
      },
      {
        id: "age-calculator",
        name: "Age & Date Calculator",
        description: "Calculate age, date differences, and more",
        category: "converters",
      },
      {
        id: "job-experience-calculator",
        name: "Job Experience Calculator",
        description: "Calculate work experience in years, months, and days — add multiple jobs for a combined total",
        category: "converters",
      },
      {
        id: "recipe-scaler",
        name: "Recipe Scaler",
        description: "Scale recipe ingredients up or down",
        category: "converters",
      },
      {
        id: "percentage-calculator",
        name: "Percentage Calculator",
        description:
          "Calculate percentages, percentage change, and what-percent-of",
        category: "converters",
      },
      {
        id: "gpa-calculator",
        name: "GPA Calculator",
        description:
          "Calculate your grade point average from courses and credits",
        category: "converters",
      },
      {
        id: "roman-numeral-converter",
        name: "Roman Numeral Converter",
        description: "Convert between numbers and Roman numerals",
        category: "converters",
      },
      {
        id: "random-number-generator",
        name: "Random Number Generator",
        description:
          "Generate random numbers in any range, with or without duplicates",
        category: "converters",
      },
      {
        id: "final-grade-calculator",
        name: "Final Grade Calculator",
        description: "Find the score you need on your final exam to finish the course with the grade you want",
        category: "converters",
      },
      {
        id: "book-spine-calculator",
        name: "Book Spine and Cover Size Calculator",
        description: "Estimate spine width and full wrap-cover size with bleed for KDP, IngramSpark and Lulu, with a to-scale layout preview.",
        category: "converters",
      },
      {
        id: "print-royalty-calculator",
        name: "Print Cost and Royalty Calculator",
        description: "Work out the printing cost of a paperback, what you earn per copy at a list price, the price for a target royalty and the copies needed to earn back your costs.",
        category: "converters",
      },
    ],
  },
  {
    id: "tax-calculators",
    name: "Tax Calculators",
    description:
      "Estimate sales tax, VAT, and income tax for the US, UK, India, and more.",
    icon: Landmark,
    color: "oklch(0.55 0.2 350)",
    tools: [
      {
        id: "sales-tax-calculator",
        name: "Sales Tax Calculator",
        description: "Calculate US sales tax with quick state rate presets",
        category: "tax-calculators",
        region: "US",
      },
      {
        id: "vat-calculator",
        name: "VAT Calculator",
        description: "Calculate VAT for the UK, EU, and other countries",
        category: "tax-calculators",
      },
      {
        id: "gst-calculator",
        name: "GST Calculator",
        description: "India GST calculator: CGST, SGST, IGST and UTGST, with amount in words",
        category: "tax-calculators",
      },
      {
        id: "self-employment-tax",
        name: "Self-Employment Tax Calculator",
        description:
          "Calculate US self-employment Social Security and Medicare tax",
        category: "tax-calculators",
        region: "US",
      },
      {
        id: "state-tax-calculator",
        name: "State Tax Calculator",
        description: "Estimate US state income tax by state",
        category: "tax-calculators",
        region: "US",
      },
      {
        id: "income-tax-calculator",
        name: "Income Tax Calculator",
        description:
          "Calculate total annual US income tax including federal, state, and FICA",
        category: "tax-calculators",
        region: "US",
      },
      {
        id: "india-income-tax",
        name: "India Income Tax Calculator",
        description:
          "Compare tax liability under India's old vs new tax regime",
        category: "tax-calculators",
        region: "India",
      },
      {
        id: "uk-tax-calculator",
        name: "UK Tax Calculator",
        description:
          "Calculate UK income tax and National Insurance contributions",
        category: "tax-calculators",
        region: "UK",
      },
    ],
  },
  {
    id: "finance-calculators",
    name: "Finance & Retirement",
    description:
      "Plan loans, mortgages, savings, and retirement with confidence.",
    icon: PiggyBank,
    color: "oklch(0.6 0.15 100)",
    tools: [
      {
        id: "loan-calculator",
        name: "Loan / EMI Calculator",
        description: "Calculate loan payments and amortization, with a dated payoff schedule",
        category: "finance-calculators",
      },
      {
        id: "mortgage-calculator",
        name: "Mortgage Calculator",
        description:
          "Estimate monthly mortgage payments including taxes and insurance, with a dated payoff schedule",
        category: "finance-calculators",
      },
      {
        id: "paycheck-calculator",
        name: "Paycheck Calculator",
        description:
          "Estimate take-home pay after federal tax and FICA withholding",
        category: "finance-calculators",
      },
      {
        id: "discount-calculator",
        name: "Discount Calculator",
        description:
          "Calculate sale prices and savings from a discount percentage",
        category: "finance-calculators",
      },
      {
        id: "compound-interest",
        name: "Compound Interest Calculator",
        description:
          "Calculate compound interest growth with regular contributions, with an optional start date",
        category: "finance-calculators",
      },
      {
        id: "sip-calculator",
        name: "SIP Calculator",
        description: "Calculate future value of a Systematic Investment Plan, with an optional start date",
        category: "finance-calculators",
      },
      {
        id: "social-security-calculator",
        name: "Social Security Calculator",
        description:
          "Estimate your monthly Social Security retirement benefit at different claiming ages",
        category: "finance-calculators",
        region: "US",
      },
      {
        id: "401k-calculator",
        name: "401(k) Calculator",
        description:
          "Project your 401(k) balance at retirement including employer match, with an optional start date",
        category: "finance-calculators",
        region: "US",
      },
      {
        id: "rmd-calculator",
        name: "RMD Calculator",
        description:
          "Calculate your required minimum distribution using the IRS Uniform Lifetime Table",
        category: "finance-calculators",
        region: "US",
      },
      {
        id: "fire-calculator",
        name: "FIRE Calculator",
        description:
          "Calculate your financial independence number and years to reach it, with an optional start date",
        category: "finance-calculators",
      },
    ],
  },
  {
    id: "health-calculators",
    name: "Health Calculators",
    description:
      "BMI, calories, body fat, and other everyday health estimates.",
    icon: HeartPulse,
    color: "oklch(0.55 0.15 175)",
    tools: [
      {
        id: "bmi-calculator",
        name: "BMI Calculator",
        description: "Calculate your Body Mass Index (BMI)",
        category: "health-calculators",
      },
      {
        id: "body-fat-calculator",
        name: "Body Fat Calculator",
        description: "Estimate body fat percentage using the US Navy method",
        category: "health-calculators",
      },
      {
        id: "bmr-macro-calculator",
        name: "BMR & Macro Calculator",
        description:
          "Calculate basal metabolic rate and daily macronutrient targets",
        category: "health-calculators",
      },
      {
        id: "calorie-calculator",
        name: "Calorie Calculator",
        description:
          "Calculate daily calorie needs for weight loss, maintenance, or gain",
        category: "health-calculators",
      },
      {
        id: "ideal-weight-calculator",
        name: "Ideal Weight Calculator",
        description:
          "Estimate ideal body weight using established medical formulas",
        category: "health-calculators",
      },
      {
        id: "pregnancy-due-date",
        name: "Pregnancy Due Date Calculator",
        description: "Estimate your due date and current week of pregnancy",
        category: "health-calculators",
      },
      {
        id: "period-calculator",
        name: "Period & Ovulation Calculator",
        description:
          "Estimate your next periods, ovulation day and fertile window with a colour-coded calendar",
        category: "health-calculators",
      },
      {
        id: "pregnancy-scan-calculator",
        name: "Pregnancy Scan Date Calculator",
        description:
          "See typical date windows for pregnancy scans and screening from your dates",
        category: "health-calculators",
      },
      {
        id: "water-intake-calculator",
        name: "Water Intake Calculator",
        description: "Calculate recommended daily water intake",
        category: "health-calculators",
      },
    ],
  },
  {
    id: "qr-tools",
    name: "QR Codes & Barcodes",
    description:
      "Generate, customize, and scan QR codes and barcodes instantly.",
    icon: QrCode,
    color: "oklch(0.5 0.15 200)",
    tools: [
      {
        id: "qr-generator",
        name: "QR Code Generator",
        description: "Generate QR codes for URLs, text, WiFi, vCard",
        category: "qr-tools",
      },
      {
        id: "custom-qr",
        name: "Custom QR Code",
        description: "Create QR codes with custom colors, logos, and shapes",
        category: "qr-tools",
      },
      {
        id: "bulk-qr",
        name: "Bulk QR Generator",
        description: "Generate hundreds of QR codes at once and download them as one ZIP",
        category: "qr-tools",
      },
      {
        id: "qr-scanner",
        name: "QR & Barcode Scanner",
        description: "Scan QR codes and barcodes using your camera",
        category: "qr-tools",
      },
      {
        id: "barcode-generator",
        name: "Barcode Generator",
        description: "Make a scannable barcode (Code 128, EAN-13, UPC-A, Code 39, ITF-14) for labels, packaging or shipping",
        category: "qr-tools",
      },
    ],
  },
  {
    id: "business-tools",
    name: "Business & Documents",
    description:
      "Create resumes, invoices, business cards, and professional documents.",
    icon: Briefcase,
    color: "oklch(0.5 0.12 250)",
    tools: [
      {
        id: "resume-builder",
        name: "Resume / CV Builder",
        description: "Create ATS-friendly resumes with templates",
        category: "business-tools",
      },
      {
        id: "bank-statement-to-csv",
        name: "Bank Statement to CSV",
        description: "Turn the transactions in a text-based bank or card statement PDF into a checked table, then save it as CSV or Excel.",
        category: "business-tools",
      },
      {
        id: "blind-cv-redactor",
        name: "Blind CV Redactor",
        description: "Remove name, contact details, address, birth date, gender words and similar details from resume text for an unbiased first review.",
        category: "business-tools",
      },
      {
        id: "resume-keyword-matcher",
        name: "Resume Keyword Matcher",
        description: "Compare a resume with a job posting and see which keywords it repeats, which your resume already uses and which are missing.",
        category: "business-tools",
      },
      {
        id: "open-house-sign-in-sheet",
        name: "Open House Sign-In Sheet Maker",
        description: "Make a printable sign-in sheet with the columns you choose and an optional QR code, as a PDF, for open houses, showings and events.",
        category: "business-tools",
      },
      {
        id: "invoice-generator",
        name: "Invoice Generator",
        description: "Create professional invoices in seconds",
        category: "business-tools",
      },
      {
        id: "business-card",
        name: "Business Card Generator",
        description: "Design and generate business cards",
        category: "business-tools",
      },
      {
        id: "contract-templates",
        name: "Contract Templates",
        description: "Professional agreement and contract templates",
        category: "business-tools",
      },
      {
        id: "expense-tracker",
        name: "Expense Tracker",
        description: "Track and categorize expenses simply",
        category: "business-tools",
      },
      {
        id: "receipt-maker",
        name: "Receipt Maker",
        description: "Make clean, printable receipts with items, tax and totals",
        category: "business-tools",
      },
      {
        id: "government-forms-hub",
        name: "Government Forms",
        description:
          "Find official US tax and employment forms (W-4, W-9, 1099-NEC, I-9)",
        category: "business-tools",
        region: "US",
      },
      {
        id: "nda-generator",
        name: "NDA Generator",
        description: "Create a mutual non-disclosure agreement template",
        category: "business-tools",
      },
      {
        id: "lease-agreement-generator",
        name: "Lease Agreement Generator",
        description: "Create a residential lease agreement template",
        category: "business-tools",
      },
      {
        id: "power-of-attorney-generator",
        name: "Power of Attorney Generator",
        description: "Create a general power of attorney template",
        category: "business-tools",
      },
      {
        id: "bill-of-sale-generator",
        name: "Bill of Sale Generator",
        description: "Create a bill of sale template for selling an item",
        category: "business-tools",
      },
      {
        id: "promissory-note-generator",
        name: "Promissory Note Generator",
        description: "Create a promissory note template for a personal loan",
        category: "business-tools",
      },
      {
        id: "eviction-notice-generator",
        name: "Eviction Notice Generator",
        description: "Create a tenant eviction notice template",
        category: "business-tools",
        region: "US",
      },
      {
        id: "startup-name-generator",
        name: "Startup Name Generator",
        description: "Generate business name ideas from a keyword",
        category: "business-tools",
      },
      {
        id: "cover-letter-maker",
        name: "Cover Letter Maker",
        description: "Write a one-page cover letter from your results and the job, as Word or PDF",
        category: "business-tools",
      },
      {
        id: "pay-stub-generator",
        name: "Pay Stub Generator",
        description: "Make a pay stub with earnings, deductions, net pay and year to date",
        category: "business-tools",
      },
      {
        id: "proposal-builder",
        name: "Proposal Builder",
        description: "Build a client proposal with scope, timeline, prices and a signature block",
        category: "business-tools",
      },
      {
        id: "rental-application-maker",
        name: "Rental Application Maker",
        description: "Make a printable rental application for a property",
        category: "business-tools",
      },
      {
        id: "employment-offer-letter",
        name: "Employment Offer Letter Generator",
        description: "Create a professional employment offer letter template",
        category: "business-tools",
      },
      {
        id: "product-description-writer",
        name: "Product Description Writer",
        description:
          "Turn product features and benefits into draft descriptions using proven copywriting structures",
        category: "business-tools",
      },
      {
        id: "user-manual-writer",
        name: "User Manual Writer",
        description:
          "Build a structured, printable user manual with your own sections and content",
        category: "business-tools",
      },
      {
        id: "invention-disclosure-worksheet",
        name: "Invention Disclosure Worksheet",
        description:
          "Organize and date your invention details before filing or talking to a patent attorney",
        category: "business-tools",
      },
      {
        id: "quote-generator",
        name: "Quote Generator",
        description: "Create a professional price quotation as a PDF with line items, tax and a signature line",
        category: "business-tools",
      },
      {
        id: "purchase-order-generator",
        name: "Purchase Order Generator",
        description: "Create a purchase order PDF to send to a supplier with items, prices, tax and delivery details",
        category: "business-tools",
      },
      {
        id: "packing-slip-generator",
        name: "Packing Slip Generator",
        description: "Create a packing slip PDF that lists the items in a shipment with quantities and a packed checklist",
        category: "business-tools",
      },
      {
        id: "email-signature-generator",
        name: "Email Signature Generator",
        description: "Create a clean email signature with your details and links, ready to paste into any email program",
        category: "business-tools",
      },
      {
        id: "label-sheet-maker",
        name: "Label Sheet Maker",
        description: "Print addresses or names on sheets of sticky labels (Avery-style Letter and A4) or your own grid, with a test page",
        category: "business-tools",
      },
    ],
  },
  {
    id: "design-tools",
    name: "Design & Color",
    description:
      "Generate palettes, gradients, favicons, and convert between color formats.",
    icon: Palette,
    color: "oklch(0.6 0.2 330)",
    tools: [
      {
        id: "color-palette",
        name: "Color Palette Generator",
        description: "Generate beautiful color palettes",
        category: "design-tools",
      },
      {
        id: "color-converter",
        name: "HEX / RGB / HSL Converter",
        description: "Convert between color formats instantly",
        category: "design-tools",
      },
      {
        id: "gradient-generator",
        name: "Gradient Generator",
        description: "Build linear, radial and conic CSS gradients and copy the code",
        category: "design-tools",
      },
      {
        id: "favicon-generator",
        name: "Favicon Generator",
        description: "Generate favicons from an image",
        category: "design-tools",
      },
      {
        id: "logo-maker",
        name: "Logo Maker",
        description: "Create simple logos and brand marks",
        category: "design-tools",
      },
      {
        id: "mockup-generator",
        name: "Mockup Generator",
        description: "Place designs in device frame mockups",
        category: "design-tools",
      },
      {
        id: "image-to-svg",
        name: "Image to SVG",
        description: "Convert raster images to SVG vectors",
        category: "design-tools",
      },
      {
        id: "color-picker-image",
        name: "Color Picker from Image",
        description: "Pick and copy any color from an uploaded image",
        category: "design-tools",
      },
      {
        id: "social-media-formatter",
        name: "Social Media Asset Formatter",
        description:
          "Resize one image into the correct size for every major platform",
        category: "design-tools",
      },
      {
        id: "box-shadow-generator",
        name: "Box Shadow Generator",
        description: "Visually build and copy CSS box-shadow code",
        category: "design-tools",
      },
      {
        id: "border-radius-generator",
        name: "Border Radius Generator",
        description: "Visually build and copy CSS border-radius code",
        category: "design-tools",
      },
      {
        id: "contrast-checker",
        name: "Color Contrast Checker",
        description:
          "Check WCAG AA/AAA accessibility contrast between two colors",
        category: "design-tools",
      },
      {
        id: "clip-path-generator",
        name: "CSS Clip-Path Generator",
        description: "Generate CSS clip-path shapes visually",
        category: "design-tools",
      },
      {
        id: "font-pairing-tool",
        name: "Font Pairing Tool",
        description: "Browse curated heading + body font combinations",
        category: "design-tools",
      },
      {
        id: "brand-kit",
        name: "Brand Kit",
        description: "Turn one brand colour into a palette, shade scale, contrast checks, font pairing and a brand sheet PDF with CSS and tokens",
        category: "design-tools",
      },
    ],
  },
  {
    id: "security-tools",
    name: "Security & Developer",
    description:
      "Generate passwords, encode/decode data, format JSON, and test regex.",
    icon: Shield,
    color: "oklch(0.5 0.15 160)",
    tools: [
      {
        id: "password-strength",
        name: "Password Generator & Checker",
        description: "Generate strong passwords and check strength",
        category: "security-tools",
      },
      {
        id: "password-generator",
        name: "Password Generator",
        description: "Generate strong, secure random passwords",
        category: "security-tools",
      },
      {
        id: "base64",
        name: "Base64 Encode / Decode",
        description: "Encode or decode Base64 strings",
        category: "security-tools",
      },
      {
        id: "json-formatter",
        name: "JSON / YAML Formatter",
        description: "Format, validate, and beautify JSON & YAML",
        category: "security-tools",
      },
      {
        id: "regex-tester",
        name: "Regex Tester",
        description: "Test and debug regular expressions",
        category: "security-tools",
      },
      {
        id: "url-encoder",
        name: "URL Encoder / Decoder",
        description: "Encode or decode URLs and query strings",
        category: "security-tools",
      },
      {
        id: "hash-generator",
        name: "Hash Generator",
        description: "Generate MD5, SHA-1, SHA-256 hashes",
        category: "security-tools",
      },
      {
        id: "jwt-decoder",
        name: "JWT Decoder",
        description: "Decode and inspect JSON Web Tokens",
        category: "security-tools",
      },
      {
        id: "uuid-generator",
        name: "UUID Generator",
        description: "Generate random UUIDs and unique keys",
        category: "security-tools",
      },
      {
        id: "diff-checker",
        name: "Diff Checker",
        description:
          "Compare two blocks of text or code and highlight differences",
        category: "security-tools",
      },
      {
        id: "binary-ascii-converter",
        name: "Binary / Hex / ASCII Converter",
        description:
          "Convert text between binary, hexadecimal, and decimal ASCII codes",
        category: "security-tools",
      },
      {
        id: "timestamp-converter",
        name: "Unix Timestamp Converter",
        description: "Convert between Unix timestamps and human-readable dates",
        category: "security-tools",
      },
      {
        id: "cron-builder",
        name: "Cron Expression Builder",
        description: "Build and understand cron expressions in plain English",
        category: "security-tools",
      },
      {
        id: "http-status-reference",
        name: "HTTP Status Code Reference",
        description: "Look up what any HTTP status code means",
        category: "security-tools",
      },
      {
        id: "html-entity-converter",
        name: "HTML Entity Encoder / Decoder",
        description: "Encode or decode HTML entities like &amp; and &lt; in text",
        category: "security-tools",
      },
      {
        id: "css-js-minifier",
        name: "CSS & JS Minifier",
        description: "Minify CSS and JavaScript to reduce file size",
        category: "security-tools",
      },
      {
        id: "user-agent-parser",
        name: "User-Agent Parser",
        description:
          "Parse a user-agent string into browser, OS, and device info",
        category: "security-tools",
      },
      {
        id: "sql-formatter",
        name: "SQL Formatter",
        description: "Format and indent SQL queries for PostgreSQL, MySQL, SQL Server, Oracle, BigQuery and more",
        category: "security-tools",
      },
      {
        id: "curl-converter",
        name: "cURL Converter",
        description: "Convert cURL commands to JavaScript fetch or Python requests code",
        category: "security-tools",
      },
      {
        id: "subnet-calculator",
        name: "Subnet Calculator",
        description: "Work out network, broadcast, usable range and masks for any IPv4 subnet, and split into smaller subnets",
        category: "security-tools",
      },
      {
        id: "chmod-calculator",
        name: "Chmod Calculator",
        description: "Convert Unix file permissions between checkboxes, octal and rwx text and copy the chmod command",
        category: "security-tools",
      },
      {
        id: "string-escape",
        name: "String Escape / Unescape",
        description: "Escape or unescape text for JSON, JavaScript, Python, HTML, URL, CSV, regex, SQL and shell",
        category: "security-tools",
      },
    ],
  },
  {
    id: "seo-tools",
    name: "SEO Tools",
    description:
      "Generate meta tags, check keywords, create social media content.",
    icon: Search,
    color: "oklch(0.55 0.18 130)",
    tools: [
      {
        id: "meta-tag-gen",
        name: "Meta Tag Generator",
        description: "Generate SEO-optimized meta tags",
        category: "seo-tools",
      },
      {
        id: "keyword-density",
        name: "Keyword Density Checker",
        description: "Analyze keyword density in your content",
        category: "seo-tools",
      },
      {
        id: "hashtag-generator",
        name: "Hashtag Generator",
        description: "Generate relevant hashtags for social media",
        category: "seo-tools",
      },
      {
        id: "caption-generator",
        name: "Social Caption Generator",
        description: "Social media caption templates and ideas",
        category: "seo-tools",
      },
      {
        id: "robots-sitemap",
        name: "Robots.txt & Sitemap Generator",
        description: "Generate robots.txt and XML sitemaps",
        category: "seo-tools",
      },
      {
        id: "og-preview",
        name: "OG / Twitter Card Preview",
        description: "Preview Open Graph and Twitter cards",
        category: "seo-tools",
      },
      {
        id: "schema-generator",
        name: "Schema Markup Generator",
        description:
          "Generate JSON-LD structured data for Article, Product, FAQ, LocalBusiness, and Recipe schema",
        category: "seo-tools",
      },
      {
        id: "site-launch-kit",
        name: "Site Launch Kit",
        description: "Make robots.txt, sitemap.xml, head tags, structured data, a web manifest and security.txt for a new website in one ZIP",
        category: "seo-tools",
      },
    ],
  },
  {
    id: "file-tools",
    name: "File & Misc",
    description:
      "ZIP files, convert audio, process spreadsheets, and more utilities.",
    icon: FolderArchive,
    color: "oklch(0.55 0.12 70)",
    tools: [
      {
        id: "zip-unzip",
        name: "ZIP / UnZIP Files",
        description: "Compress and extract ZIP archives in browser",
        category: "file-tools",
      },
      {
        id: "json-csv-converter",
        name: "JSON and CSV Converter",
        description: "Turn JSON into a CSV table for Excel or Sheets, or CSV back into JSON, with nested fields handled",
        category: "file-tools",
      },
      {
        id: "mail-merge-pdf",
        name: "Mail Merge to PDF",
        description: "Turn a CSV or pasted table and a template into one letter, certificate or notice per row, as one PDF or a ZIP of PDFs.",
        category: "file-tools",
      },
      {
        id: "csv-converter",
        name: "Excel / CSV Converter",
        description: "Convert between Excel and CSV formats",
        category: "file-tools",
      },
      {
        id: "meme-generator",
        name: "Meme Generator",
        description: "Create memes with custom text and images",
        category: "file-tools",
      },
      {
        id: "ocr-tool",
        name: "OCR (Image to Text)",
        description: "Extract text from images using OCR",
        category: "file-tools",
      },
      {
        id: "file-size-converter",
        name: "File Size Converter",
        description: "Convert between file size units",
        category: "file-tools",
      },
      {
        id: "yt-thumbnail",
        name: "YouTube Thumbnail Downloader",
        description: "Download YouTube video thumbnails",
        category: "file-tools",
      },
      {
        id: "epub-checker",
        name: "EPUB Checker",
        description: "Check an .epub in your browser for structure, metadata, navigation, missing files, alt text, cover and accessibility problems.",
        category: "file-tools",
      },
      {
        id: "epub-maker",
        name: "EPUB Maker",
        description: "Turn .docx, .md and .txt chapters into a finished EPUB 3 e-book with cover, details and a reading preview.",
        category: "file-tools",
      },
      {
        id: "onix-generator",
        name: "ONIX Metadata Generator",
        description: "Fill in a book metadata form and get a checked ONIX 3.0 XML file with ISBN-13 validation, prices, subjects and a live preview.",
        category: "file-tools",
      },
    ],
  },
  {
    id: "3d-tools",
    name: "3D & CAD Files",
    description:
      "Convert and inspect STL, OBJ, and PLY 3D mesh files, right in your browser.",
    icon: Box,
    color: "oklch(0.55 0.18 250)",
    tools: [
      {
        id: "mesh-3d-converter",
        name: "3D Mesh Converter (STL/OBJ/PLY/GLB/3MF)",
        description: "Convert between STL, OBJ, PLY (ASCII or binary), GLB and 3MF, with size and 3D-printing checks",
        category: "3d-tools",
      },
      {
        id: "stl-ascii-binary-converter",
        name: "STL ASCII / Binary Converter",
        description: "Convert STL files between ASCII and binary format",
        category: "3d-tools",
      },
      {
        id: "mesh-3d-viewer",
        name: "3D Mesh Viewer",
        description:
          "Preview STL, OBJ, PLY, GLB and 3MF models as a lit 3D shape you can orbit, with size and printability checks",
        category: "3d-tools",
      },
    ],
  },
  {
    id: "ecommerce-tools",
    name: "E-commerce",
    description:
      "Calculate margins, marketplace fees, shipping costs, and break-even points.",
    icon: ShoppingCart,
    color: "oklch(0.6 0.18 25)",
    tools: [
      {
        id: "profit-margin-calculator",
        name: "Profit Margin Calculator",
        description:
          "Find your margin and markup, or the price you need to charge for a target margin",
        category: "ecommerce-tools",
      },
      {
        id: "marketplace-fee-calculator",
        name: "Marketplace Fee Calculator",
        description:
          "Estimate Amazon, Etsy, eBay, Walmart, Mercari, TikTok Shop or Shopify fees and your real net profit",
        category: "ecommerce-tools",
      },
      {
        id: "shipping-cost-estimator",
        name: "Shipping Cost Estimator",
        description: "Estimate shipping costs including dimensional weight",
        category: "ecommerce-tools",
      },
      {
        id: "break-even-calculator",
        name: "Break-Even Calculator",
        description: "Calculate how many units you need to sell to break even",
        category: "ecommerce-tools",
      },
      {
        id: "digital-download-packager",
        name: "Digital Download Packager",
        description: "Pack printables, templates or fonts into one ZIP with a license and how-to-use note, and make a square cover image for the listing.",
        category: "ecommerce-tools",
      },
      {
        id: "listing-photo-pack",
        name: "Listing Photo Pack",
        description: "Size product photos for Etsy, eBay, Amazon or your own shop in one go, with notes on anything too small",
        category: "ecommerce-tools",
      },
      {
        id: "seller-studio",
        name: "Seller Studio",
        description: "A launch board for your whole shop: photo checks and bulk resizing, title and tag checks, a shop audit, price finder and profit after your fees. Monthly plan with a 7-day trial.",
        category: "ecommerce-tools",
      },
      {
        id: "seller-pricing-desk",
        name: "Seller Pricing Desk",
        description: "Price each product after marketplace fees, payment fees, ads and shipping, and see your profit at the current price",
        category: "ecommerce-tools",
      },
      {
        id: "etsy-listing-checker",
        name: "Etsy Title and Tag Checker",
        description: "Check an Etsy listing title against the 140-character limit and your tags against the 13-tag, 20-character limits, with repeat and coverage checks.",
        category: "ecommerce-tools",
      },
    ],
  },
  {
    id: "travel-tools",
    name: "Travel Planning",
    description:
      "Plan trips, budget travel costs, build itineraries, and pack smarter.",
    icon: Plane,
    color: "oklch(0.6 0.15 200)",
    tools: [
      {
        id: "travel-budget-planner",
        name: "Travel Budget Planner",
        description:
          "Plan and total your trip costs across lodging, food, transport, and more",
        category: "travel-tools",
      },
      {
        id: "travel-itinerary-planner",
        name: "Travel Itinerary Planner",
        description: "Build a day-by-day plan for your trip",
        category: "travel-tools",
      },
      {
        id: "travel-packing-list",
        name: "Travel Packing List",
        description:
          "Generate a packing checklist by trip type, or build your own",
        category: "travel-tools",
      },
      {
        id: "travel-countdown-planner",
        name: "Travel Countdown & To-Do Planner",
        description:
          "See how many days until departure and when to handle each pre-trip task",
        category: "travel-tools",
      },
    ],
  },
  {
    id: "event-tools",
    name: "Event Planning",
    description:
      "Budget, schedule, and track guests for weddings, parties, and corporate events.",
    icon: PartyPopper,
    color: "oklch(0.62 0.2 350)",
    tools: [
      {
        id: "event-budget-planner",
        name: "Event Budget Planner",
        description:
          "Plan a budget in any currency, record expenses, and see planned versus spent by category, with Excel and CSV export",
        category: "event-tools",
      },
      {
        id: "event-checklist-planner",
        name: "Event Checklist & Timeline Planner",
        description:
          "Plan any event with timelines counted back from your date, your own tasks, owners and due dates, and CSV or Excel export",
        category: "event-tools",
      },
      {
        id: "guest-list-tracker",
        name: "Guest List & RSVP Tracker",
        description: "Track guests, replies, plus-ones, diet, access and room needs, with CSV import and export",
        category: "event-tools",
      },
      {
        id: "name-badge-maker",
        name: "Name Badge Maker",
        description: "Print event name badges from your guest list or typed names, with optional QR passes, on Letter or A4 badge sheets",
        category: "event-tools",
      },
      {
        id: "certificate-maker",
        name: "Certificate Maker",
        description: "Make certificates of attendance or appreciation for every guest, as one PDF or a ZIP, on Letter or A4",
        category: "event-tools",
      },
      {
        id: "catering-quantity-calculator",
        name: "Catering Quantity Calculator",
        description: "Work out how much food and drink to order from your head count, event type and hours, with a safety margin",
        category: "event-tools",
      },
      {
        id: "event-profit-ledger",
        name: "Event Profit Ledger",
        description: "Turn your event income and costs into a profit and loss statement with planned versus actual, tax lines and per-head figures",
        category: "event-tools",
      },
      {
        id: "receipt-to-expense-sheet",
        name: "Receipt to Expense Sheet",
        description: "Turn receipt photos or pasted text into expense rows with vendor, date, total and tax, then add them to your project",
        category: "event-tools",
      },
      {
        id: "timesheet-generator",
        name: "Timesheet Generator",
        description: "Make printable weekly, fortnightly or monthly timesheets for staff, crew or volunteers, one page each",
        category: "event-tools",
      },
      {
        id: "event-closure-report-maker",
        name: "Event Closure Report Maker",
        description: "Build the closing report for an event: attendance, programme, income and costs against budget, vendors and lessons",
        category: "event-tools",
      },
      {
        id: "booth-layout-planner",
        name: "Booth Layout Planner",
        description: "Plan an exhibition hall floor: drag booths, stages and aisles, catch overlaps, then download a PDF, PNG or CSV",
        category: "event-tools",
      },
      {
        id: "exhibitor-booth-tracker",
        name: "Exhibitor and Booth Tracker",
        description: "Track exhibitors, booth numbers, fees and payments, and spot double-booked booths and unpaid balances",
        category: "event-tools",
      },
      {
        id: "visitor-lead-capture-sheet",
        name: "Visitor Lead Capture Sheet",
        description: "Capture booth visitors as hot, warm or cold leads on your phone and download a CSV, or print blank lead sheets",
        category: "event-tools",
      },
      {
        id: "merchandise-order-sheet",
        name: "Merchandise Quantity and Order Sheet",
        description: "Work out how many T-shirts, bags and giveaways to order, with size splits, minimum orders and costs, as a supplier order sheet",
        category: "event-tools",
      },
      {
        id: "badge-pass-maker",
        name: "Badge and Pass Maker",
        description: "Add your guests, print name badges with QR passes, then check people in at the door. All three steps share one guest list",
        category: "event-tools",
      },
      {
        id: "invite-calendar-maker",
        name: "Invite and Calendar Maker",
        description: "Build an invitation card and a matching calendar invite for your guests from one project",
        category: "event-tools",
      },
      {
        id: "expense-capture-sheet",
        name: "Expense Capture Sheet",
        description: "Capture receipts as expenses, check them against your budget and see the profit statement",
        category: "event-tools",
      },
      {
        id: "agenda-cue-sheet-builder",
        name: "Agenda and Cue Sheet Builder",
        description: "Plan the schedule, print the agenda, run the show from a cue sheet and keep speakers on time",
        category: "event-tools",
      },
      {
        id: "rooming-dietary-sheet",
        name: "Rooming and Dietary Sheet",
        description: "Collect dietary and access needs from your guest list and print labels for the people who need them",
        category: "event-tools",
      },
      {
        id: "vendor-quote-comparison",
        name: "Vendor Quote Comparison",
        description: "Compare vendor quotes for the same item side by side, accept one and add it to your budget",
        category: "event-tools",
      },
      {
        id: "quote-comparison-builder",
        name: "Quote Comparison Builder",
        description: "Collect vendor quotes, compare them, accept one, add it to your budget and raise a purchase order",
        category: "event-tools",
      },
      {
        id: "guest-manager",
        name: "Guest Manager",
        description: "Run the whole guest side of an event: list, needs, badges with QR passes, labels and a door check that runs on your device",
        category: "event-tools",
      },
      {
        id: "invitation-automator",
        name: "Invitation Automator",
        description: "Make the invitation card, calendar invite, event web page and QR codes from one set of event details",
        category: "event-tools",
      },
      {
        id: "programme-show-day-planner",
        name: "Programme and Show-Day Planner",
        description: "Plan the programme and run show day: schedule, agenda, cue sheet, checklist, catering quantities and stage timer",
        category: "event-tools",
      },
      {
        id: "profit-expense-manager",
        name: "Profit and Expense Manager",
        description: "Plan the budget, capture receipts, order merchandise, record crew time and read the profit statement",
        category: "event-tools",
      },
      {
        id: "event-report-generator",
        name: "Event Report Generator",
        description: "Close the event: profit statement, guest summary, a closure report PDF and attendee certificates",
        category: "event-tools",
      },
      {
        id: "rooming-list-planner",
        name: "Rooming List Planner",
        description: "Plan who stays where and when, count rooms needed each night and write the block request for a hotel",
        category: "event-tools",
      },
      {
        id: "seating-chart-planner",
        name: "Seating Chart Planner",
        description: "Seat guests at tables, keep organisations together, catch full tables and download the seating plan",
        category: "event-tools",
      },
      {
        id: "arrivals-departures-board",
        name: "Arrivals and Departures Board",
        description: "See who arrives and leaves each day from your guest list, to plan pickups, shuttles and welcome desks",
        category: "event-tools",
      },
      {
        id: "vendor-quote-manager",
        name: "Vendor Quote Manager",
        description: "Collect and compare vendor quotes, budget the winner, raise purchase orders and check deliveries",
        category: "event-tools",
      },
      {
        id: "travel-planner",
        name: "Travel Planner",
        description: "Plan travel for an event: arrivals and departures, itinerary, time zones, travel budget and packing list",
        category: "event-tools",
      },
      {
        id: "accommodation-manager",
        name: "Accommodation Manager",
        description: "Manage hotel stays: rooming list, room block request, arrivals and departures, and room labels",
        category: "event-tools",
      },
      {
        id: "seating-floor-planner",
        name: "Seating and Floor Planner",
        description: "Plan seating by table, draw the room layout and print place cards and table labels",
        category: "event-tools",
      },
      {
        id: "event-tax-summary",
        name: "Event Tax Summary",
        description: "Summarise tax charged on income and paid on costs by type and rate, in any currency. Information only",
        category: "event-tools",
      },
      {
        id: "booth-manager",
        name: "Booth Manager",
        description: "Run an exhibition floor: exhibitors and payments, booth layout, visitor lead capture and exhibitor badges",
        category: "event-tools",
      },
      {
        id: "marketing-materials-manager",
        name: "Marketing Materials Manager",
        description: "Create the invitation, flyer, banner, event web page and QR codes that promote your event",
        category: "event-tools",
      },
      {
        id: "giveaway-branding-manager",
        name: "Giveaway and Branding Manager",
        description: "Run a fair prize draw and prepare merchandise, certificates and labels for your event",
        category: "event-tools",
      },
      {
        id: "event-suite",
        name: "Event Suite",
        description: "Run all your events under one account: one subscription, pick each event type, add a starter plan, and use thirteen products on one shared project",
        category: "event-tools",
      },
      {
        id: "giveaway-winner-picker",
        name: "Giveaway Winner Picker",
        description: "Draw giveaway or raffle winners fairly from a list of names, with backups, extra tickets and a record of the draw",
        category: "event-tools",
      },
      {
        id: "focus-stage-timer",
        name: "Focus and Stage Timer",
        description: "A big full-screen countdown for speakers that turns amber and red, plus a focus timer with work and break rounds",
        category: "event-tools",
      },
      {
        id: "agenda-builder",
        name: "Agenda Builder",
        description: "Turn your schedule into a printable event programme PDF with times, presenters and rooms",
        category: "event-tools",
      },
      {
        id: "run-of-show-cue-sheet",
        name: "Run-of-Show Cue Sheet",
        description: "Make a crew cue sheet with times, lengths, who and where, notes and tick boxes, and check for overlaps",
        category: "event-tools",
      },
      {
        id: "event-landing-page-generator",
        name: "Event Landing Page Generator",
        description: "Make a one-page event website with programme, venue, RSVP button and add-to-calendar link as a single HTML file",
        category: "event-tools",
      },
      {
        id: "calendar-invite-maker",
        name: "Calendar Invite Maker",
        description: "Make a calendar file (.ics) for one event or your whole event schedule, with the right time in every time zone",
        category: "event-tools",
      },
      {
        id: "dietary-access-summary",
        name: "Dietary and Access Summary",
        description: "Count dietary needs and allergies and list access needs from your guest list, as a sheet for the caterer and venue",
        category: "event-tools",
      },
      {
        id: "event-day-schedule",
        name: "Event Day-Of Schedule Builder",
        description: "Build a multi-day event schedule with rooms, presenters and overlap warnings, with CSV import and Excel export",
        category: "event-tools",
      },
    ],
  },
  {
    id: "animation-tools",
    name: "Animation & Avatars",
    description:
      "Emoji tools, a parametric avatar builder, and sprite sheet utilities.",
    icon: Smile,
    color: "oklch(0.65 0.2 50)",
    tools: [
      {
        id: "emoji-picker",
        name: "Emoji Picker & Copy",
        description: "Search and copy emoji by keyword",
        category: "animation-tools",
      },
      {
        id: "emoji-unicode-converter",
        name: "Emoji to Unicode / HTML Converter",
        description:
          "Convert emoji to Unicode, HTML entities, and JS escape codes",
        category: "animation-tools",
      },
      {
        id: "avatar-builder",
        name: "Avatar Builder",
        description:
          "Build a custom avatar from hair, eyes, and color options — no photo needed",
        category: "animation-tools",
      },
      {
        id: "sprite-sheet-tool",
        name: "Sprite Sheet Generator & Splitter",
        description:
          "Combine images into a sprite sheet, or split a sheet into individual frames",
        category: "animation-tools",
      },
    ],
  },
  {
    id: "household-tools",
    name: "Household & Family",
    description:
      "Monthly budgets, grocery and shopping lists, and home inventory tracking.",
    icon: Home,
    color: "oklch(0.6 0.15 145)",
    tools: [
      {
        id: "monthly-budget-planner",
        name: "Monthly Budget Planner",
        description:
          "Track monthly income and expenses and see your surplus or shortfall",
        category: "household-tools",
      },
      {
        id: "grocery-list-planner",
        name: "Grocery List Planner",
        description: "Build a grocery list organized by aisle",
        category: "household-tools",
      },
      {
        id: "shopping-list-planner",
        name: "Shopping List Planner",
        description:
          "Track a shopping list against a budget, with running totals",
        category: "household-tools",
      },
      {
        id: "inventory-tracker",
        name: "Home Inventory Tracker",
        description:
          "Track household items by category and location, with low-stock alerts",
        category: "household-tools",
      },
    ],
  },
  {
    id: "college-tools",
    name: "College & Education",
    description:
      "Estimate college costs, student loan payoff, and 529 savings plans.",
    icon: GraduationCap,
    color: "oklch(0.55 0.18 265)",
    tools: [
      {
        id: "college-cost-calculator",
        name: "College Cost Calculator",
        description:
          "Estimate total cost of attendance over multiple years, net of scholarships, with an optional start date",
        category: "college-tools",
      },
      {
        id: "student-loan-calculator",
        name: "Student Loan Repayment Calculator",
        description:
          "See your monthly payment, payoff date, and total interest, with optional extra payments",
        category: "college-tools",
      },
      {
        id: "college-529-calculator",
        name: "529 College Savings Calculator",
        description:
          "Find the monthly contribution needed to hit your college savings goal, with an optional start date",
        category: "college-tools",
      },
      {
        id: "citation-generator",
        name: "Citation Generator",
        description:
          "Generate APA, MLA, Chicago, or Harvard citations, and build a reference list",
        category: "college-tools",
      },
      {
        id: "text-similarity-checker",
        name: "Text Similarity Checker",
        description:
          "Compare two texts for overlapping phrases — not a web-search plagiarism checker",
        category: "college-tools",
      },
      {
        id: "paper-format-checker",
        name: "Paper Format Checker",
        description:
          "Check a .docx paper's margins, font, and spacing against APA, MLA, or Chicago style",
        category: "college-tools",
      },
      {
        id: "paper-submission-desk",
        name: "Paper Submission Desk",
        description: "Check a paper against a journal's limits and get a cover letter, title page and checklist ready for submission",
        category: "college-tools",
      },
    ],
  },
  {
    id: "relocation-tools",
    name: "Relocation & Moving",
    description:
      "Estimate moving costs, plan your relocation timeline, and compare cost of living.",
    icon: Truck,
    color: "oklch(0.58 0.16 30)",
    tools: [
      {
        id: "moving-cost-estimator",
        name: "Moving Cost Estimator",
        description:
          "Estimate moving costs by home size, distance, and services needed",
        category: "relocation-tools",
      },
      {
        id: "relocation-checklist-planner",
        name: "Relocation Checklist & Timeline",
        description:
          "A move-day countdown checklist, sorted by when each task is due",
        category: "relocation-tools",
      },
      {
        id: "cost-of-living-comparison",
        name: "Cost of Living Comparison",
        description:
          "Compare monthly expenses between two cities, auto-filled with real government cost-of-living data for major U.S. metros",
        category: "relocation-tools",
      },
    ],
  },
  {
    id: "marketing-design-tools",
    name: "Marketing & Print Design",
    description:
      "Menus, invitations, brochures, banners, and signage — designed and exported in your browser.",
    icon: Megaphone,
    color: "oklch(0.58 0.2 15)",
    tools: [
      {
        id: "menu-card-maker",
        name: "Menu Card Maker",
        description:
          "Design a restaurant or event menu with sections, items, and pricing",
        category: "marketing-design-tools",
      },
      {
        id: "invitation-card-maker",
        name: "Invitation Card Maker",
        description:
          "Create invitations for birthdays, weddings, and other events",
        category: "marketing-design-tools",
      },
      {
        id: "brochure-flyer-maker",
        name: "Brochure & Flyer Maker",
        description: "Design a one-page business flyer or brochure",
        category: "marketing-design-tools",
      },
      {
        id: "banner-sign-maker",
        name: "Banner & Sign Maker",
        description:
          "Design web banners, storefront signs, and posters at any custom pixel size",
        category: "marketing-design-tools",
      },
    ],
  },
  {
    id: "ai-studio",
    hidden: true,
    name: "AI Studio",
    description:
      "Chat with AI, summarize documents, generate content, and automate workflows.",
    icon: Sparkles,
    color: "oklch(0.5 0.2 280)",
    tools: [
      {
        id: "ai-chat",
        name: "AI Chat",
        description: "Chat with AI for any question or task",
        hidden: true,
        category: "ai-studio",
        isAI: true,
      },
      {
        id: "doc-summarizer",
        name: "Document Summarizer",
        description: "Summarize long documents instantly",
        hidden: true,
        category: "ai-studio",
        isAI: true,
      },
      {
        id: "content-generator",
        name: "Content Generator",
        description: "Generate emails, posts, and ideas",
        hidden: true,
        category: "ai-studio",
        isAI: true,
      },
      {
        id: "ai-workflows",
        name: "Multi-step Workflows",
        description: "Chain tools together for complex tasks",
        hidden: true,
        category: "ai-studio",
        isAI: true,
      },
      {
        id: "image-captioner",
        name: "Image Description",
        description: "Generate captions and descriptions for images",
        hidden: true,
        category: "ai-studio",
        isAI: true,
      },
      {
        id: "brainstorm",
        name: "Brainstorming Assistant",
        description: "Generate ideas and creative solutions",
        hidden: true,
        category: "ai-studio",
        isAI: true,
      },
      {
        id: "ai-rewriter",
        name: "AI Rewrite / Improve",
        description: "Rewrite and improve any text with AI",
        hidden: true,
        category: "ai-studio",
        isAI: true,
      },
      {
        id: "smart-processor",
        name: "Smart File Processor",
        description: "Intelligent file processing and routing",
        hidden: true,
        category: "ai-studio",
        isAI: true,
      },
      {
        id: "smart-encoder",
        name: "Smart Encoder / Obfuscator",
        description: "AI-assisted encoding that's genuinely harder to reverse than simple URL encoding",
        hidden: true,
        category: "ai-studio",
        isAI: true,
      },
      {
        id: "meme-video-generator",
        name: "Video & GIF Meme Generator",
        description: "Add captions and overlays to video and GIF memes, not just static images",
        hidden: true,
        category: "ai-studio",
        isAI: true,
      },
      {
        id: "fbx-converter",
        name: "FBX 3D Format Converter",
        description: "Convert 3D mesh files to and from the FBX format",
        hidden: true,
        category: "ai-studio",
        isAI: true,
      },
    ],
  },
];

export function getAllTools(): Tool[] {
  return categories.flatMap(c => c.tools);
}

/** Categories safe to show in navigation, the homepage grid, the footer
 * index, and the sitemap. Excludes anything marked `hidden` (currently just
 * AI Studio, until intentionally re-launched as a premium product) -- and within a visible category,
 * also strips out any individual tool marked `hidden` (a network-dependent
 * AI tool not meeting the "just works" bar of the rest of the site, for
 * example), so it disappears from every discovery surface without needing
 * each one fixed separately. Use `categories` instead when you need to
 * resolve a specific category/tool by ID regardless of visibility. */
export const visibleCategories: Category[] = categories
  .filter(c => !c.hidden)
  .map(c => ({ ...c, tools: c.tools.filter(t => !t.hidden) }));

export function getToolById(id: string): Tool | undefined {
  return getAllTools().find(t => t.id === id);
}

export function getCategoryById(id: string): Category | undefined {
  return categories.find(c => c.id === id);
}

import { smartSearch } from "./smartSearch";

export function searchTools(query: string): Tool[] {
  const searchableTools = visibleCategories.flatMap(c => c.tools);
  return smartSearch(query, searchableTools);
}
